(() => {
  "use strict";

  const PAGE_SELECTOR = "[data-armzone-product]";

  const qs = (selector, scope = document) =>
    scope.querySelector(selector);

  const qsa = (selector, scope = document) =>
    Array.from(scope.querySelectorAll(selector));

  const getShopifyRoot = () => {
    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : "/";

    return root.endsWith("/") ? root : `${root}/`;
  };

  const shopifyUrl = (path) =>
    `${getShopifyRoot()}${String(path).replace(/^\/+/, "")}`;

  const escapeHTML = (value = "") =>
    String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const formatMoney = (cents) => {
    const amount = (Number(cents) || 0) / 100;

    const currency =
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
        ? window.Shopify.currency.active
        : "USD";

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || "en-US",
        {
          style: "currency",
          currency,
        }
      ).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const fetchJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: "same-origin",
      ...options,
      headers: {
        Accept: "application/json",
        ...(options.body
          ? { "Content-Type": "application/json" }
          : {}),
        ...(options.headers || {}),
      },
    });

    if (!response.ok) {
      let message = `Request failed (${response.status})`;

      try {
        const data = await response.json();

        if (data.description) {
          message = data.description;
        } else if (data.message) {
          message = data.message;
        }
      } catch (error) {
        /* use default message */
      }

      throw new Error(message);
    }

    return response.json();
  };

  const initProductPage = (page) => {
    if (!page || page.dataset.azpInitialized === "true") {
      return;
    }

    page.dataset.azpInitialized = "true";

    const variantsJSON = qs(
      "[data-product-variants-json]",
      page
    );

    let variants = [];

    if (variantsJSON) {
      try {
        variants = JSON.parse(variantsJSON.textContent);
      } catch (error) {
        console.error(
          "ARMZONE: Could not read variant data.",
          error
        );
      }
    }

    const productForm = qs(
      "[data-product-form]",
      page
    );

    const variantSelect = qs(
      "[data-product-variant-select]",
      page
    );

    const optionGroups = qsa(
      "[data-option-group]",
      page
    );

    const optionButtons = qsa(
      "[data-option-button]",
      page
    );

    const priceElement = qs(
      "[data-product-price]",
      page
    );

    const comparePriceElement = qs(
      "[data-product-compare-price]",
      page
    );

    const stockElement = qs(
      "[data-product-stock]",
      page
    );

    const addButton = qs(
      "[data-product-add]",
      page
    );

    const addButtonText = qs(
      "[data-product-add-text]",
      page
    );

    const messageElement = qs(
      "[data-product-message]",
      page
    );

    const quantityInput = qs(
      "[data-product-quantity]",
      page
    );

    const quantityMinus = qs(
      "[data-product-quantity-minus]",
      page
    );

    const quantityPlus = qs(
      "[data-product-quantity-plus]",
      page
    );

    const primaryImage =
      qs(".azp-main-media.is-primary img", page) ||
      qs(".azp-main-media img", page);

    const thumbnails = qsa(
      "[data-product-thumbnail]",
      page
    );

    const progressBar = qs(
      "[data-product-scroll-progress]",
      page
    );

    if (progressBar) {
      const updateScrollProgress = () => {
        const scrollTop =
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          0;

        const available =
          document.documentElement.scrollHeight -
          window.innerHeight;

        const percent =
          available > 0
            ? Math.min(
                100,
                Math.max(
                  0,
                  (scrollTop / available) * 100
                )
              )
            : 0;

        progressBar.style.width = `${percent}%`;
      };

      updateScrollProgress();

      window.addEventListener(
        "scroll",
        updateScrollProgress,
        { passive: true }
      );

      window.addEventListener(
        "resize",
        updateScrollProgress,
        { passive: true }
      );
    }

    const setPrimaryImage = (
      src,
      alt = "",
      activeThumbnail = null
    ) => {
      if (!primaryImage || !src) return;

      primaryImage.src = src;
      primaryImage.alt = alt;
      primaryImage.removeAttribute("srcset");
      primaryImage.removeAttribute("sizes");

      thumbnails.forEach((thumb) => {
        thumb.classList.toggle(
          "active",
          thumb === activeThumbnail
        );
      });
    };

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener("click", () => {
        setPrimaryImage(
          thumbnail.dataset.imageSrc,
          thumbnail.dataset.imageAlt || "",
          thumbnail
        );
      });
    });

    const galleryImages = qsa(
      "[data-gallery-image]",
      page
    );

    galleryImages.forEach((galleryButton) => {
      galleryButton.addEventListener("click", () => {
        const image = qs("img", galleryButton);

        if (!image || image === primaryImage) {
          return;
        }

        setPrimaryImage(
          image.currentSrc || image.src,
          image.alt || ""
        );
      });
    });

    const getSelectedOptions = () => {
      const selected = [];

      optionGroups.forEach((group) => {
        const position = Number(
          group.dataset.optionPosition
        );

        const active = qs(
          "[data-option-button].active",
          group
        );

        if (active) {
          selected[position - 1] =
            active.dataset.optionValue;
        }
      });

      return selected;
    };

    const variantMatchesOptions = (
      variant,
      selectedOptions
    ) => {
      return selectedOptions.every(
        (value, index) => {
          if (
            value === undefined ||
            value === null ||
            value === ""
          ) {
            return true;
          }

          const variantValue =
            variant[`option${index + 1}`];

          return variantValue === value;
        }
      );
    };

    const findSelectedVariant = () => {
      const selectedOptions =
        getSelectedOptions();

      return (
        variants.find((variant) =>
          variantMatchesOptions(
            variant,
            selectedOptions
          )
        ) || null
      );
    };

    const updateOptionAvailability = () => {
      const currentSelections =
        getSelectedOptions();

      optionButtons.forEach((button) => {
        const position =
          Number(button.dataset.optionPosition) - 1;

        const candidateSelections = [
          ...currentSelections,
        ];

        candidateSelections[position] =
          button.dataset.optionValue;

        const hasAvailableVariant =
          variants.some(
            (variant) =>
              variant.available &&
              variantMatchesOptions(
                variant,
                candidateSelections
              )
          );

        button.classList.toggle(
          "unavailable",
          !hasAvailableVariant
        );

        button.setAttribute(
          "aria-disabled",
          hasAvailableVariant
            ? "false"
            : "true"
        );
      });
    };

    const updateVariantUI = (
      variant,
      updateUrl = true
    ) => {
      if (!variant) {
        if (addButton) {
          addButton.disabled = true;
        }

        if (addButtonText) {
          addButtonText.textContent =
            "UNAVAILABLE";
        }

        if (stockElement) {
          stockElement.textContent =
            "UNAVAILABLE";
        }

        return;
      }

      if (variantSelect) {
        variantSelect.value =
          String(variant.id);
      }

      if (priceElement) {
        priceElement.textContent =
          formatMoney(variant.price);
      }

      if (comparePriceElement) {
        const comparePrice =
          Number(variant.compare_at_price) || 0;

        const actualPrice =
          Number(variant.price) || 0;

        if (
          comparePrice > actualPrice
        ) {
          comparePriceElement.hidden = false;
          comparePriceElement.textContent =
            formatMoney(comparePrice);
        } else {
          comparePriceElement.hidden = true;
          comparePriceElement.textContent = "";
        }
      }

      if (stockElement) {
        stockElement.textContent =
          variant.available
            ? "AVAILABLE"
            : "SOLD OUT";
      }

      if (addButton) {
        addButton.disabled =
          !variant.available;

        addButton.dataset.variantId =
          variant.id;
      }

      if (addButtonText) {
        addButtonText.textContent =
          variant.available
            ? "ADD TO BAG"
            : "SOLD OUT";
      }

      if (
        variant.featured_image &&
        primaryImage
      ) {
        setPrimaryImage(
          variant.featured_image,
          primaryImage.alt || ""
        );
      }

      if (
        updateUrl &&
        window.history &&
        window.history.replaceState
      ) {
        try {
          const url = new URL(
            window.location.href
          );

          url.searchParams.set(
            "variant",
            variant.id
          );

          window.history.replaceState(
            {},
            "",
            url.toString()
          );
        } catch (error) {
          /* URL update isn't essential */
        }
      }

      updateOptionAvailability();
    };

    optionButtons.forEach((button) => {
      button.addEventListener("click", () => {
        if (
          button.getAttribute(
            "aria-disabled"
          ) === "true"
        ) {
          return;
        }

        const group = button.closest(
          "[data-option-group]"
        );

        if (!group) return;

        qsa(
          "[data-option-button]",
          group
        ).forEach((otherButton) => {
          otherButton.classList.toggle(
            "active",
            otherButton === button
          );
        });

        const selectedLabel = qs(
          "[data-option-selected]",
          group
        );

        if (selectedLabel) {
          selectedLabel.textContent =
            button.dataset.optionValue;
        }

        const variant =
          findSelectedVariant();

        updateVariantUI(variant);
      });
    });

    const normaliseQuantity = () => {
      if (!quantityInput) return 1;

      let quantity =
        parseInt(quantityInput.value, 10);

      if (
        !Number.isFinite(quantity) ||
        quantity < 1
      ) {
        quantity = 1;
      }

      quantityInput.value = quantity;

      return quantity;
    };

    if (quantityMinus) {
      quantityMinus.addEventListener(
        "click",
        () => {
          if (!quantityInput) return;

          const current =
            normaliseQuantity();

          quantityInput.value =
            Math.max(1, current - 1);
        }
      );
    }

    if (quantityPlus) {
      quantityPlus.addEventListener(
        "click",
        () => {
          if (!quantityInput) return;

          quantityInput.value =
            normaliseQuantity() + 1;
        }
      );
    }

    if (quantityInput) {
      quantityInput.addEventListener(
        "change",
        normaliseQuantity
      );

      quantityInput.addEventListener(
        "blur",
        normaliseQuantity
      );
    }

    const overlay = qs(
      "[data-product-overlay]",
      page
    );

    const menuDrawer = qs(
      "[data-product-menu-drawer]",
      page
    );

    const bagDrawer = qs(
      "[data-product-bag-drawer]",
      page
    );

    const bagBody = qs(
      "[data-product-bag-body]",
      page
    );

    const bagFooter = qs(
      "[data-product-bag-footer]",
      page
    );

    const bagCountElements = qsa(
      "[data-product-bag-count]",
      page
    );

    const subtotalElements = qsa(
      "[data-product-subtotal]",
      page
    );

    const lockPage = () => {
      document.documentElement.style.overflow =
        "hidden";

      document.body.style.overflow =
        "hidden";
    };

    const unlockPage = () => {
      document.documentElement.style.overflow =
        "";

      document.body.style.overflow =
        "";
    };

    const closeDrawers = () => {
      if (menuDrawer) {
        menuDrawer.classList.remove(
          "open"
        );

        menuDrawer.setAttribute(
          "aria-hidden",
          "true"
        );
      }

      if (bagDrawer) {
        bagDrawer.classList.remove(
          "open"
        );

        bagDrawer.setAttribute(
          "aria-hidden",
          "true"
        );
      }

      if (overlay) {
        overlay.classList.remove(
          "active"
        );
      }

      unlockPage();
    };

    const openMenu = () => {
      if (!menuDrawer) return;

      if (bagDrawer) {
        bagDrawer.classList.remove(
          "open"
        );

        bagDrawer.setAttribute(
          "aria-hidden",
          "true"
        );
      }

      menuDrawer.classList.add(
        "open"
      );

      menuDrawer.setAttribute(
        "aria-hidden",
        "false"
      );

      if (overlay) {
        overlay.classList.add(
          "active"
        );
      }

      lockPage();
    };

    const openBag = () => {
      if (!bagDrawer) return;

      if (menuDrawer) {
        menuDrawer.classList.remove(
          "open"
        );

        menuDrawer.setAttribute(
          "aria-hidden",
          "true"
        );
      }

      bagDrawer.classList.add(
        "open"
      );

      bagDrawer.setAttribute(
        "aria-hidden",
        "false"
      );

      if (overlay) {
        overlay.classList.add(
          "active"
        );
      }

      lockPage();
    };

    const updateCartNumbers = (cart) => {
      bagCountElements.forEach(
        (element) => {
          element.textContent =
            cart.item_count;
        }
      );

      subtotalElements.forEach(
        (element) => {
          element.textContent =
            formatMoney(
              cart.total_price
            );
        }
      );
    };

    const renderCart = (cart) => {
      updateCartNumbers(cart);

      if (!bagBody) return;

      if (
        !cart.items ||
        cart.items.length === 0
      ) {
        bagBody.innerHTML = `
          <div class="azp-empty-bag">
            <span>YOUR BAG / 00</span>

            <h2>
              Your bag<br>
              is empty.
            </h2>

            <p>
              Explore the collection and carry
              a piece of the story forward.
            </p>
          </div>
        `;

        if (bagFooter) {
          bagFooter.hidden = true;
        }

        return;
      }

      if (bagFooter) {
        bagFooter.hidden = false;
      }

      bagBody.innerHTML = cart.items
        .map((item) => {
          const variantTitle =
            item.variant_title &&
            item.variant_title !==
              "Default Title"
              ? item.variant_title
              : "";

          const imageHTML =
            item.image
              ? `
                <img
                  src="${escapeHTML(
                    item.image
                  )}"
                  alt="${escapeHTML(
                    item.product_title
                  )}"
                  loading="lazy"
                >
              `
              : `
                <div
                  style="
                    width:110px;
                    height:130px;
                    background:#dedad3;
                  "
                ></div>
              `;

          return `
            <article class="azp-bag-item">

              ${imageHTML}

              <div>

                <h3>
                  ${escapeHTML(
                    item.product_title
                  )}
                </h3>

                ${
                  variantTitle
                    ? `
                      <span
                        class="azp-bag-item-variant"
                      >
                        ${escapeHTML(
                          variantTitle
                        )}
                      </span>
                    `
                    : ""
                }

                <strong>
                  ${formatMoney(
                    item.final_line_price
                  )}
                </strong>

                <div
                  class="azp-bag-quantity"
                >

                  <button
                    type="button"
                    data-cart-change
                    data-cart-key="${escapeHTML(
                      item.key
                    )}"
                    data-cart-quantity="${
                      item.quantity - 1
                    }"
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>

                  <span>
                    ${item.quantity}
                  </span>

                  <button
                    type="button"
                    data-cart-change
                    data-cart-key="${escapeHTML(
                      item.key
                    )}"
                    data-cart-quantity="${
                      item.quantity + 1
                    }"
                    aria-label="Increase quantity"
                  >
                    +
                  </button>

                </div>

              </div>

            </article>
          `;
        })
        .join("");
    };

    const loadCart = async () => {
      try {
        const cart = await fetchJSON(
          shopifyUrl("cart.js")
        );

        renderCart(cart);

        return cart;
      } catch (error) {
        console.error(
          "ARMZONE cart load error:",
          error
        );

        return null;
      }
    };

    const changeCartQuantity = async (
      key,
      quantity
    ) => {
      try {
        const cart = await fetchJSON(
          shopifyUrl("cart/change.js"),
          {
            method: "POST",
            body: JSON.stringify({
              id: key,
              quantity: Math.max(
                0,
                Number(quantity) || 0
              ),
            }),
          }
        );

        renderCart(cart);
      } catch (error) {
        console.error(
          "ARMZONE cart update error:",
          error
        );
      }
    };

    const showMessage = (
      message,
      isError = false
    ) => {
      if (!messageElement) return;

      messageElement.hidden = false;
      messageElement.textContent =
        message;

      messageElement.style.color =
        isError ? "#8b2525" : "#111";
    };

    const hideMessage = () => {
      if (!messageElement) return;

      messageElement.hidden = true;
      messageElement.textContent = "";
    };

    if (productForm) {
      productForm.addEventListener(
        "submit",
        async (event) => {
          event.preventDefault();

          hideMessage();

          const variant =
            findSelectedVariant();

          if (!variant) {
            showMessage(
              "Please select an available option.",
              true
            );

            return;
          }

          if (!variant.available) {
            showMessage(
              "This option is currently sold out.",
              true
            );

            return;
          }

          const quantity =
            normaliseQuantity();

          if (addButton) {
            addButton.disabled = true;
          }

          if (addButtonText) {
            addButtonText.textContent =
              "ADDING...";
          }

          try {
            await fetchJSON(
              shopifyUrl(
                "cart/add.js"
              ),
              {
                method: "POST",
                body: JSON.stringify({
                  items: [
                    {
                      id: Number(
                        variant.id
                      ),
                      quantity,
                    },
                  ],
                }),
              }
            );

            const cart =
              await loadCart();

            if (addButtonText) {
              addButtonText.textContent =
                "ADDED TO BAG";
            }

            showMessage(
              "Added to your bag."
            );

            openBag();

            window.setTimeout(() => {
              if (addButtonText) {
                addButtonText.textContent =
                  "ADD TO BAG";
              }

              if (addButton) {
                addButton.disabled =
                  !variant.available;
              }

              hideMessage();
            }, 1000);

            return cart;
          } catch (error) {
            console.error(
              "ARMZONE add-to-cart error:",
              error
            );

            showMessage(
              error.message ||
                "Unable to add this item.",
              true
            );

            if (addButtonText) {
              addButtonText.textContent =
                "TRY AGAIN";
            }

            if (addButton) {
              addButton.disabled = false;
            }
          }
        }
      );
    }

    qsa(
      "[data-product-open-menu]",
      page
    ).forEach((button) => {
      button.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          openMenu();
        }
      );
    });

    qsa(
      "[data-product-open-bag]",
      page
    ).forEach((button) => {
      button.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          openBag();
        }
      );
    });

    qsa(
      "[data-product-close-drawer]",
      page
    ).forEach((button) => {
      button.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          closeDrawers();
        }
      );
    });

    if (overlay) {
      overlay.addEventListener(
        "click",
        closeDrawers
      );
    }

    if (bagBody) {
      bagBody.addEventListener(
        "click",
        (event) => {
          const button =
            event.target.closest(
              "[data-cart-change]"
            );

          if (!button) return;

          event.preventDefault();

          changeCartQuantity(
            button.dataset.cartKey,
            button.dataset.cartQuantity
          );
        }
      );
    }

    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Escape") {
          closeDrawers();
        }
      }
    );

    let initialVariant = null;

    if (variantSelect) {
      initialVariant =
        variants.find(
          (variant) =>
            String(variant.id) ===
            String(
              variantSelect.value
            )
        ) || null;
    }

    if (!initialVariant) {
      initialVariant =
        findSelectedVariant();
    }

    if (!initialVariant) {
      initialVariant =
        variants.find(
          (variant) =>
            variant.available
        ) ||
        variants[0] ||
        null;
    }

    updateVariantUI(
      initialVariant,
      false
    );

    loadCart();
  };

  const initAllProductPages = (
    scope = document
  ) => {
    if (
      scope.matches &&
      scope.matches(PAGE_SELECTOR)
    ) {
      initProductPage(scope);
    }

    qsa(
      PAGE_SELECTOR,
      scope
    ).forEach(initProductPage);
  };

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initAllProductPages();
      }
    );
  } else {
    initAllProductPages();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initAllProductPages(
        event.target
      );
    }
  );
})();